import React, { useRef, useState, useEffect } from "react";
import { FormHelperText } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./TextEditor.scss";

export default function TextEditor({
  id = "",
  readOnly = false,
  value,
  onChange,
  isError = false,
  errorMsg = "",
  placeholder,
  toolbarOptions = [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
  ],
}) {
  const MAX_CHAR_LIMIT = 2000;
  const quillRef = useRef(null);
  const [editorValue, setEditorValue] = useState(value || "");
  const [charCount, setCharCount] = useState(0);
  const [pasteMessage, setPasteMessage] = useState("");

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleChange = (content) => {
    const plainText = stripHtml(content);
    const length = plainText.length;

    if (length <= MAX_CHAR_LIMIT) {
      setEditorValue(content);
      setCharCount(length);
      const elementId = quillRef.current?.getEditor?.().root?.id || "";
      onChange(content, quillRef.current, elementId);
    } else {
      const quill = quillRef.current.getEditor();
      quill.deleteText(MAX_CHAR_LIMIT, quill.getLength());
    }
  };

  useEffect(() => {
    if (quillRef.current && id) {
      const editor = quillRef.current.getEditor();
      if (editor?.root) {
        editor.root.setAttribute("id", id);
      }
    }
  }, [id]);

  useEffect(() => {
    setEditorValue(value || "");
    const plainText = stripHtml(value || "");
    setCharCount(plainText.length);
  }, [value]);

  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
      
        if (quill) {
          quill.focus();
          quill.blur();
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);


  const handleBlur = () => {
    const editorInstance = quillRef.current?.getEditor?.();
    const content = editorInstance?.root?.innerHTML || "";
    const elementId = editorInstance?.root?.id || "";
    onChange(content, editorInstance, elementId);
  };

  const handleFocus = () => {
 
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill) {
        // Small delay to ensure editor is fully focused and ready
        setTimeout(() => {
          quill.focus();
        }, 10);
      }
    }
  };

  const showPasteMessage = (msg) => {
    setPasteMessage(msg);
    setTimeout(() => setPasteMessage(""), 3000);
  };



  const formats = ["bold", "italic", "underline", "list", "bullet", "align"];

  return (
    <div className="overrideQuillEditor">
      {isError && stripHtml(editorValue).length === 0 && (
        <FormHelperText className="errorText">{errorMsg}</FormHelperText>
      )}

      <ReactQuill
        id={id}
        ref={quillRef}
        value={editorValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        modules={{ toolbar: toolbarOptions }}
        formats={formats}
        placeholder={placeholder || "Start typing..."}
        theme="snow"
        readOnly={readOnly}
      />

      <div className="char-limit-msg">
        {charCount} / {MAX_CHAR_LIMIT} Characters
      </div>
      {pasteMessage && <div className="editor-error-msg">{pasteMessage}</div>}
    </div>
  );
}
